import React from 'react';
import { ProductVariant } from '../../../lib/api/models';

import { T } from '@tolgee/react';

interface ProductVariantsProps {
  variants: ProductVariant[];
  selectedVariant?: ProductVariant;
  onVariantSelect: (variant: ProductVariant) => void;
}

export const ProductVariants: React.FC<ProductVariantsProps> = ({
  variants,
  selectedVariant,
  onVariantSelect
}) => {
  if (!variants || variants.length === 0) {
    return null;
  }

  const getVariantPrice = (variant: ProductVariant) => {
    // El backend ya devuelve el precio correcto según la tarifa del usuario
    // Solo necesitamos usar el campo 'price' que ya está filtrado
    return variant.price || 0;
  };

  const handleVariantSelect = (variant: ProductVariant) => {
    onVariantSelect(variant);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900"><T keyName="product.select_size">Selecciona tamaño</T></h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {variants.map((variant, index) => {
          const isSelected = selectedVariant
            ? variant.sku === selectedVariant.sku
            : index === 0;
          const price = getVariantPrice(variant);
          // El backend ya maneja descuentos - discountPrice solo existe para admins
          const hasDiscount = false; // Los descuentos ya están aplicados en 'price'
          const isOutOfStock = variant.stock === 0;
          const isLowStock = variant.stock > 0 && variant.stock <= 5;

          return (
            <button
              key={variant.sku}
              onClick={() => handleVariantSelect(variant)}
              disabled={isOutOfStock}
              className={`relative p-4 border-2 rounded-xl text-left transition-all duration-200 ${isSelected
                  ? 'border-black bg-gray-50 ring-2 ring-black ring-offset-2'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                } ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {/* Badge superior derecho */}
              <div className="absolute -top-2 -right-2">
                {hasDiscount ? (
                  <div className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                    -{Math.round((1 - price / variant.price) * 100)}%
                  </div>
                ) : isOutOfStock ? (
                  <div className="bg-gray-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-md">
                    <T keyName="product.out_of_stock_badge">Agotado</T>
                  </div>
                ) : isLowStock ? (
                  <div className="bg-orange-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-md">
                    <T keyName="product.low_stock_badge" params={{ stock: variant.stock.toString() }}>{`¡${variant.stock} left!`}</T>
                  </div>
                ) : null}
              </div>

              <div className="space-y-3">
                {/* Nombre de la variante */}
                <h4 className="font-semibold text-gray-900 pr-8">{variant.name}</h4>

                {/* Referencia/SKU */}
                {variant.sku && (
                  <p className="text-xs text-gray-500">Ref: {variant.sku}</p>
                )}

                {/* Precio */}
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {price ? `${price.toFixed(2)}€` : <span className="text-lg"><T keyName="product.check_price">Consultar</T></span>}
                  </span>

                  {hasDiscount && variant.price ? (
                    <span className="text-sm text-gray-400 line-through">
                      {variant.price.toFixed(2)}€
                    </span>
                  ) : null}
                </div>

                {/* Stock disponible */}
                {!isOutOfStock ? (
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isLowStock ? 'bg-orange-500' : 'bg-green-500'}`} />
                    <span className="text-sm text-gray-600">
                      {isLowStock ? `Solo ${variant.stock} disponibles` : 'En stock'}
                    </span>
                  </div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProductVariants;
